import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { getOrders, getOrderStatusAPI, getEnvAPI } from "./ordersControlar";
import { createBoardSync } from "./boardSync";
import { getChatsAPI } from "./chatControlar";
import { postToNative, isNativeApp } from "./nativeBridge";

const GlobalStateContext = createContext();

// 掃描槍輸入在中文輸入法（注音等）下，keydown 會變成組字事件（keyCode 229），
// e.key 拿到的是注音符號而非條碼字元；e.code 是「實體鍵位」，不受輸入法影響，
// 因此用 e.code 對照表還原掃描槍實際送出的 ASCII 字元。
const BARCODE_KEY_CODE_MAP = {
    Digit1: ['1', '!'], Digit2: ['2', '@'], Digit3: ['3', '#'], Digit4: ['4', '$'],
    Digit5: ['5', '%'], Digit6: ['6', '^'], Digit7: ['7', '&'], Digit8: ['8', '*'],
    Digit9: ['9', '('], Digit0: ['0', ')'],
    Minus: ['-', '_'], Equal: ['=', '+'],
    BracketLeft: ['[', '{'], BracketRight: [']', '}'], Backslash: ['\\', '|'],
    Semicolon: [';', ':'], Quote: ["'", '"'], Backquote: ['`', '~'],
    Comma: [',', '<'], Period: ['.', '>'], Slash: ['/', '?'],
    Space: [' ', ' '],
    Numpad0: ['0', '0'], Numpad1: ['1', '1'], Numpad2: ['2', '2'], Numpad3: ['3', '3'],
    Numpad4: ['4', '4'], Numpad5: ['5', '5'], Numpad6: ['6', '6'], Numpad7: ['7', '7'],
    Numpad8: ['8', '8'], Numpad9: ['9', '9'], NumpadDecimal: ['.', '.'],
    NumpadAdd: ['+', '+'], NumpadSubtract: ['-', '-'],
    NumpadMultiply: ['*', '*'], NumpadDivide: ['/', '/'],
};
for (let i = 0; i < 26; i++) {
    const upper = String.fromCharCode(65 + i);
    BARCODE_KEY_CODE_MAP[`Key${upper}`] = [upper.toLowerCase(), upper];
}

const charFromBarcodeKeyEvent = (e) => {
    // 英文輸入法下 e.key 就是正確字元，直接使用（也涵蓋 e.code 為空的舊裝置）
    if (!e.isComposing && e.keyCode !== 229 && e.key?.length === 1) {
        const code = e.key.charCodeAt(0);
        return code >= 0x20 && code <= 0x7e ? e.key : '';
    }
    const mapped = BARCODE_KEY_CODE_MAP[e.code];
    if (!mapped) return '';
    return e.shiftKey ? mapped[1] : mapped[0];
};

export const GlobalStateProvider = ({ children }) => {
    const [currentTab, setCurrentTab] = useState(0);
    const [systemData, setSystemData] = useState(null);
    const [globalOrder, setGlobalOrder] = useState(null);
    const [todayOrders, setTodayOrders] = useState(null);
    const [foodData, setFoodData] = useState(null);
    const [ordersStatusList, setOrderStatusList] = useState(null);
    const ordersStatusListRef = useRef(null);
    const [newOrderNotices, setNewOrderNotices] = useState({});
    const [ws, setWs] = useState(null);
    const [urlType, setUrlType] = useState(1);
    const urlTypeRef = useRef(urlType);
    const [barcode, setBarcode] = useState('');
    const [fullScreenAlert, setFullScreenAlert] = useState(null);
    const [chatUnread, setChatUnread] = useState(0);      // LINE 訊息未讀總數（側欄徽章）
    const [pendingMakeCount, setPendingMakeCount] = useState(0); // 出餐狀況「未完成」訂單數（選單紅字）
    const [chatEvent, setChatEvent] = useState(null);     // 最新一筆聊天室 WS 事件（訊息中心頁面用來即時刷新）
    const [kioskDisplayEvent, setKioskDisplayEvent] = useState(null); // POS 點餐同步到自助點餐 iPad 的客顯事件

    const audioRef = useRef(null);           // 音頻引用
    const beepIntervalRef = useRef(null);    // 重複播放計時器引用
    const beepTimeoutRef = useRef(null);     // 自動停止聲音計時器
    const newOrderTimeoutsRef = useRef({});
    const audioContextRef = useRef(null);    // AudioContext 引用

    useEffect(() => {
        postToNative('unread', { count: chatUnread });
    }, [chatUnread]);

    // 只播網頁音效；App 內由原生播放聲音（spec §4.3），網頁不重複播
    const playWebAudio = useCallback(() => {
        if (isNativeApp()) return;
        if (audioRef.current) {
            audioRef.current.volume = 1;
            audioRef.current.currentTime = 0; // 归零重新播放
            audioRef.current.play().catch((error) => {
                console.log("音頻播放失敗:", error);
            });
        }
    }, []);

    const playBeep = useCallback(() => {
        postToNative('beep', { kind: 'notify' });
        playWebAudio();
    }, [playWebAudio]);

    const stopBeep = useCallback(() => {
        postToNative('beep', { kind: 'stop' });
        if (beepIntervalRef.current) {
            clearInterval(beepIntervalRef.current);
            beepIntervalRef.current = null;
        }
        if (beepTimeoutRef.current) {
            clearTimeout(beepTimeoutRef.current);
            beepTimeoutRef.current = null;
        }
    }, []);

    const startBeep = useCallback((duration = null) => {
        postToNative('beep', { kind: 'alert' });
        if (!beepIntervalRef.current) {
            beepIntervalRef.current = setInterval(playWebAudio, 1500);
        }
        playWebAudio();

        if (duration) {
            if (beepTimeoutRef.current) {
                clearTimeout(beepTimeoutRef.current);
            }
            beepTimeoutRef.current = setTimeout(() => {
                stopBeep();
            }, duration);
        }
    }, [playWebAudio, stopBeep]);

    const closeFullScreenAlert = useCallback(() => {
        setFullScreenAlert(null);
        stopBeep();
    }, [stopBeep]);

    const markNewOrders = useCallback((orders) => {
        const previousOrders = ordersStatusListRef.current || [];
        if (!previousOrders.length || !orders?.length) {
            return;
        }

        const previousOrderIds = new Set(previousOrders.map((order) => order.order_id));
        const previousDetailIds = new Set(
            previousOrders.flatMap((order) => order.detail?.map((item) => item.id) || [])
        );

        const incomingOrderIds = orders
            .filter((order) => {
                const hasNewOrder = !previousOrderIds.has(order.order_id);
                const hasNewItem = order.detail?.some((item) => !previousDetailIds.has(item.id));
                return hasNewOrder || hasNewItem;
            })
            .map((order) => order.order_id);

        if (!incomingOrderIds.length) {
            return;
        }

        setNewOrderNotices((prev) => {
            const next = { ...prev };
            incomingOrderIds.forEach((orderId) => {
                next[orderId] = Date.now();
            });
            return next;
        });

        incomingOrderIds.forEach((orderId) => {
            if (newOrderTimeoutsRef.current[orderId]) {
                clearTimeout(newOrderTimeoutsRef.current[orderId]);
            }
            newOrderTimeoutsRef.current[orderId] = setTimeout(() => {
                setNewOrderNotices((prev) => {
                    const next = { ...prev };
                    delete next[orderId];
                    return next;
                });
                delete newOrderTimeoutsRef.current[orderId];
            }, 10000);
        });
    }, []);

    // 出餐看板重抓一律經過這裡（推播、輪詢、進頁、重連），只套用最新且沒被本機改動蓋過的結果
    const boardSyncRef = useRef(null);
    if (!boardSyncRef.current) {
        boardSyncRef.current = createBoardSync({
            fetchList: (type) => getOrderStatusAPI(type, { silent: true }),
            getType: () => urlTypeRef.current,
            applyList: (list, type) => {
                if (type === 1) markNewOrders(list);
                ordersStatusListRef.current = list;
                setOrderStatusList(list);
            },
        });
    }
    const refreshOrderStatus = boardSyncRef.current.refresh;
    const mutateOrderStatus = boardSyncRef.current.mutate;

    // 解鎖音頻播放：使用 click 與 touchstart 事件，同時 Resume AudioContext
    useEffect(() => {
        // 初始化 AudioContext（如果支持）
        if (!audioContextRef.current && window.AudioContext) {
            audioContextRef.current = new AudioContext();
        }
        if (audioRef.current === null) {
            audioRef.current = new Audio('/beep/beep.mp3');
            audioRef.current.volume = 1;
            audioRef.current.load(); // 預載音頻文件
        }

        const unlockAudio = () => {
            // 若 AudioContext 處於 suspended 狀態，則恢復它
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                audioContextRef.current.resume().then(() => {
                    console.log("AudioContext 已恢復");
                });
            }
            // 靜音播放一次以解鎖自動播放權限
            if (audioRef.current) {
                audioRef.current.muted = true;
                const playResult = audioRef.current.play();
                if (playResult && typeof playResult.catch === 'function') {
                    playResult.catch(() => {}).finally(() => {
                        audioRef.current.muted = false;
                    });
                } else {
                    audioRef.current.muted = false;
                }
            }
            document.removeEventListener('click', unlockAudio);
            document.removeEventListener('touchstart', unlockAudio);
        };

        document.addEventListener('click', unlockAudio);
        document.addEventListener('touchstart', unlockAudio);

        return () => {
            document.removeEventListener('click', unlockAudio);
            document.removeEventListener('touchstart', unlockAudio);
        };
    }, []);

    // render 時就同步：子元件（出餐看板）的 effect 比這裡的 effect 先跑，切頁籤當下就要讀到新值
    urlTypeRef.current = urlType;

    useEffect(() => {
        ordersStatusListRef.current = ordersStatusList;
    }, [ordersStatusList]);

    // 出餐看板清單是「未完成」時，數量直接沿用（推播/輪詢都會更新它）
    useEffect(() => {
        if (urlType === 1 && Array.isArray(ordersStatusList)) {
            setPendingMakeCount(ordersStatusList.length);
        }
    }, [ordersStatusList, urlType]);

    // 看板停在「已出單」時，另外抓「未完成」數量給選單紅字
    const refreshPendingMakeCount = useCallback(async () => {
        if (window.location.pathname.startsWith('/kiosk')) return;
        const list = await getOrderStatusAPI(1, { silent: true });
        if (Array.isArray(list)) setPendingMakeCount(list.length);
    }, []);

    useEffect(() => {
        return () => {
            stopBeep();
            Object.values(newOrderTimeoutsRef.current).forEach(clearTimeout);
        };
    }, [stopBeep]);

    // 重抓商品資料（meal/menu/food），補齊 WS 推播漏接期間的狀態變更
    // （登入後只載一次 + 純靠推播，斷線/休眠期間改的暫停販售狀態會一直停留在舊值）
    const refreshProductData = useCallback(async () => {
        try {
            const response = await getEnvAPI();
            const store = response?.store;
            if (store && Array.isArray(store.meal) && Array.isArray(store.menu)) {
                setSystemData((prevSystemData) =>
                    prevSystemData
                        ? {
                              ...prevSystemData,
                              meal: store.meal,
                              menu: store.menu,
                              food: store.food || [],
                          }
                        : prevSystemData
                );
            }
        } catch (error) {
            // 重抓失敗維持現有資料即可，不打擾操作
        }
    }, []);

    // 重抓 LINE 未讀總數：徽章平常靠 WS 已讀事件更新，斷線漏接會停在舊數字
    const refreshChatUnread = useCallback(async () => {
        // kiosk 帳號被後端擋 POS 員工 API（403），ws 重連時也不要打
        if (window.location.pathname.startsWith('/kiosk')) return;
        try {
            const response = await getChatsAPI({ type: "unread", page: 0 });
            if (typeof response?.unread_total === 'number') {
                setChatUnread(response.unread_total);
            }
        } catch (error) {
            // 靜默失敗，維持現有數字
        }
    }, []);

    // 以下省略部分 WebSocket 初始化與其他邏輯，可照原有方式保留
    const initializeWebSocket = useCallback((position_id) => {
        const socket = new WebSocket(
            (process.env.NODE_ENV === "production" ? 'wss://www.ciaowin35.com/ws' : 'ws://debug.ciaowin35.com:9601') + '?id=' + position_id
        );

        socket.onopen = () => {
            console.log('WebSocket連接成功');
            socket.send(JSON.stringify({ message: 'Hello from client!' }));
            // 重連成功代表中間可能漏接推播，重抓商品資料與未讀數
            if (wsHasConnectedRef.current) {
                refreshProductData();
                refreshChatUnread();
                // 斷線期間別台改過的出餐進度也要補回來，不要等 60 秒輪詢
                if (!window.location.pathname.startsWith('/kiosk')) refreshOrderStatus();
            }
            wsHasConnectedRef.current = true;
        };

        socket.onmessage = (event) => {
            const message = JSON.parse(event.data);
            console.log('收到的消息:', message, position_id);

            if (message.type === 'kioskDisplay') {  //POS 點餐 → iPad 客顯核對畫面
                setKioskDisplayEvent({ ...message, receivedAt: Date.now() });
                return;
            }

            if (window.location.pathname.startsWith('/kiosk') && message.type !== 'updateProduct') {
                return;
            }

            if (message.type === 'updateOrder') {
                getOrders({ now: 1 }).then((response) => {
                    setTodayOrders(response);
                });
                // 外送員指派會讓訂單從製作中(3)進入運送中(4)，也要同步刷新出餐看板，
                // 否則卡片會留到下一次 60 秒輪詢才消失。
                refreshOrderStatus();
                if (urlTypeRef.current !== 1) refreshPendingMakeCount();
            }

            if (message.type === 'updateReservation') {
                window.dispatchEvent(new CustomEvent('reservation-updated'));
            }

            if (message.type === 'updateProduct') {
                const productData = message.data;
                if (
                    !productData ||
                    !Array.isArray(productData.meal) ||
                    !Array.isArray(productData.menu) ||
                    !Array.isArray(productData.food)
                ) {
                    console.warn('忽略格式不正確的商品更新:', message);
                    return;
                }

                setSystemData((prevSystemData) => ({
                    ...prevSystemData,
                    meal: productData.meal,
                    menu: productData.menu,
                    food: productData.food,
                }));
            }

            if (message.type === 'updateMake' || message.type === 'updateOrder') {
                // 取餐叫號面板（QuickActions/PickupBoardDialog）開著時據此立即刷新
                window.dispatchEvent(new CustomEvent('pickup-board-updated'));
            }

            if (message.type === 'updateMake') {
                refreshOrderStatus();
                if (urlTypeRef.current !== 1) refreshPendingMakeCount();
            }

            if (message.type === 'alert') {
                const account = (localStorage.getItem("account") || "").toUpperCase();
                const shouldUseCompactAlert = ["POS2", "POS3"].includes(account);
                const compactDuration = 10000;
                startBeep(shouldUseCompactAlert ? compactDuration : null);
                const option = {
                    autoClose: false,
                    onClose: () => {
                        stopBeep();
                    },
                };
                const compactOption = {
                    autoClose: compactDuration,
                    closeOnClick: true,
                    position: "top-right",
                    style: {
                        background: "rgba(179,0,0,0.95)",
                        color: "white",
                        minWidth: "280px",
                        fontSize: "18px",
                        fontWeight: 700,
                    },
                    onClose: () => {
                        stopBeep();
                    },
                };

                if (shouldUseCompactAlert || !message.big) {
                    message.status === 1
                        ? toast.info(message.msg, shouldUseCompactAlert ? compactOption : option)
                        : toast.error(message.msg, shouldUseCompactAlert ? compactOption : option);
                } else {
                    setFullScreenAlert({
                        message: message.msg,
                        status: message.status,
                        receivedAt: new Date(),
                    });
                }
            }

            if(message.type === 'stopBeep'){  //關閉聲音通知
                stopBeep();
                setFullScreenAlert(null);
            }

            if (message.type === 'updateChat') {  //LINE 訊息中心
                if (typeof message.unread_total === 'number') {
                    setChatUnread(message.unread_total);
                }
                setChatEvent({ ...message, receivedAt: Date.now() });

                //side=1 客人傳入訊息：結帳台(POS1)發出明顯通知，其他機台只更新徽章
                if (message.side === 1 && Number(position_id) === 1) {
                    playBeep();
                    if (!window.location.pathname.startsWith('/chat')) {
                        toast.info(`💬 ${message.name || 'LINE客人'}：${message.text || ''}\n（點擊開啟訊息中心）`, {
                            autoClose: 15000,
                            closeOnClick: true,
                            position: "top-right",
                            style: {
                                background: "#1D4ED8",
                                color: "white",
                                minWidth: "300px",
                                fontSize: "17px",
                                fontWeight: 700,
                                whiteSpace: "pre-line",
                                cursor: "pointer",
                            },
                            onClick: () => {
                                window.dispatchEvent(new CustomEvent('pos-navigate', { detail: '/chat' }));
                            },
                        });
                    }
                }
            }

            if (message.type === 'notify') {
                playBeep();
                message.status === 1
                    ? toast.info(message.msg)
                    : toast.error(message.msg);
            }
        };

        socket.onerror = (error) => {
            console.error('WebSocket錯誤:', error);
        };

        socket.onclose = () => {
            console.log('WebSocket連接關閉，重試連接');
            setTimeout(() => initializeWebSocket(position_id), 500);
        };

        setWs(socket);
    }, [playBeep, refreshChatUnread, refreshPendingMakeCount, refreshProductData, startBeep, stopBeep]);

    const wsInitializedRef = useRef(false);
    const wsHasConnectedRef = useRef(false);
    useEffect(() => {
        if (systemData?.position_id && !wsInitializedRef.current) {
            wsInitializedRef.current = true;
            initializeWebSocket(systemData.position_id);
        }
    }, [initializeWebSocket, systemData?.position_id]);

    // 登入後先抓一次 LINE 未讀數，讓側欄徽章在沒有新訊息進來時也正確
    const chatUnreadInitializedRef = useRef(false);
    useEffect(() => {
        if (systemData && systemData.client_mode !== 'kiosk' && !chatUnreadInitializedRef.current) {
            chatUnreadInitializedRef.current = true;
            refreshChatUnread();
        }
    }, [systemData, refreshChatUnread]);

    // 未出餐單數：登入後抓一次，之後每分鐘同步（推播漏接時的保險）
    const pendingMakeEnabled = Boolean(systemData) && systemData.client_mode !== 'kiosk';
    useEffect(() => {
        if (!pendingMakeEnabled) return undefined;
        refreshPendingMakeCount();
        const intervalId = setInterval(refreshPendingMakeCount, 60000);
        return () => clearInterval(intervalId);
    }, [pendingMakeEnabled, refreshPendingMakeCount]);

    // 心跳檢測
    useEffect(() => {
        const interval = setInterval(() => {
            if (ws && ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'heartbeat' }));
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [ws]);

    const sendMessage = (message) => {
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify(message));
        } else {
            console.error('WebSocket連接未開啟，無法發送消息');
        }
    };
    // 以 window keydown 緩衝條碼槍輸入，避免焦點與中文輸入法的干擾。
    // 條碼槍通常以 <30ms 間隔連續送出可見 ASCII 字元，最後以 Enter 結束。
    const BarcodeScanner = () => {
        useEffect(() => {
            let buffer = '';
            let lastTime = 0;
            const MIN_LEN = 4;        // 低於此長度視為誤觸，不處理
            const RESET_GAP_MS = 100; // 間隔過久視為新一輪

            const onKeyDown = (e) => {
                const target = e.target;
                const isEditableTarget =
                    target instanceof HTMLInputElement ||
                    target instanceof HTMLTextAreaElement ||
                    target?.isContentEditable;
                if (isEditableTarget) return;

                const now = Date.now();
                const gap = now - lastTime;
                lastTime = now;

                // Enter 用 e.code 判斷：中文輸入法下 e.key 可能是 'Process'
                if (e.key === 'Enter' || e.code === 'Enter' || e.code === 'NumpadEnter') {
                    if (buffer.length >= MIN_LEN) {
                        e.preventDefault();
                        setBarcode(buffer);
                    }
                    buffer = '';
                    return;
                }

                // 中文輸入法下改由實體鍵位還原字元，掃描槍輸入不再被組字吃掉
                const ch = charFromBarcodeKeyEvent(e);
                if (!ch) return;

                if (gap > RESET_GAP_MS) buffer = '';
                buffer += ch;
                // 吞掉事件，避免字元流入其他焦點欄位
                e.preventDefault();
            };

            window.addEventListener('keydown', onKeyDown, true);
            return () => window.removeEventListener('keydown', onKeyDown, true);
        }, []);

        return null;
    };

    return (
        <GlobalStateContext.Provider value={{
            globalOrder,
            setGlobalOrder,
            systemData,
            setSystemData,
            refreshProductData,
            foodData,
            setFoodData,
            ordersStatusList,
            setOrderStatusList,
            refreshOrderStatus,
            mutateOrderStatus,
            newOrderNotices,
            playBeep,
            toast,
            sendMessage,
            currentTab,
            setCurrentTab,
            todayOrders,
            setTodayOrders,
            urlType,
            setUrlType,
            BarcodeScanner,
            barcode,
            setBarcode,
            chatUnread,
            setChatUnread,
            pendingMakeCount,
            chatEvent,
            kioskDisplayEvent
        }}>
            {children}
            {fullScreenAlert && (
                <div
                    className={`pos-alert-overlay ${fullScreenAlert.status === 1 ? "is-info" : "is-error"}`}
                    role="alertdialog"
                    aria-modal="true"
                    aria-labelledby="pos-alert-title"
                    onClick={closeFullScreenAlert}
                >
                    <div className="pos-alert-panel" onClick={(event) => event.stopPropagation()}>
                        <div className="pos-alert-topline">
                            <div className="pos-alert-badge">
                                {fullScreenAlert.status === 1 ? "通知" : "警示"}
                            </div>
                            <button
                                type="button"
                                className="pos-alert-close"
                                aria-label="關閉通知"
                                onClick={closeFullScreenAlert}
                            >
                                ×
                            </button>
                        </div>
                        <div className="pos-alert-content">
                            <div className="pos-alert-icon" aria-hidden="true">!</div>
                            <h1 id="pos-alert-title" className="pos-alert-title">
                                {fullScreenAlert.status === 1 ? "新通知" : "需要處理"}
                            </h1>
                            <div className="pos-alert-message">
                                {fullScreenAlert.message}
                            </div>
                            <div className="pos-alert-footer">
                                <span>
                                    {fullScreenAlert.receivedAt.toLocaleTimeString("zh-TW", {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })}
                                </span>
                                <button type="button" onClick={closeFullScreenAlert}>
                                    我知道了
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </GlobalStateContext.Provider>
    );
};

export const useGlobalOrder = () => useContext(GlobalStateContext);
